import React from 'react';
import { Link } from 'react-router-dom';
import { Wordmark } from '../components';

const LegalPage = ({ title, children }) => (
  <div style={{ minHeight: '100vh', background: 'var(--bg)', color: 'var(--ink)' }}>
    <header style={{
      position: 'sticky', top: 0, zIndex: 100,
      borderBottom: '1px solid var(--line)',
      background: 'oklch(1 0 0 / 0.85)',
      backdropFilter: 'blur(12px)',
      padding: '0 24px',
    }}>
      <div style={{ maxWidth: 800, margin: '0 auto', height: 60, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Link to="/" style={{ textDecoration: 'none' }}>
          <Wordmark size={22} />
        </Link>
        <Link to="/"
          style={{ fontSize: 13, color: 'var(--ink-2)', textDecoration: 'none', fontFamily: 'var(--font-mono)', letterSpacing: '.06em' }}>
          ← Back to home
        </Link>
      </div>
    </header>

    <div style={{ maxWidth: 800, margin: '0 auto', padding: '60px 24px 100px' }}>
      <div style={{ fontFamily: 'var(--font-mono)', fontSize: 11, letterSpacing: '.14em', textTransform: 'uppercase', color: 'var(--ink-3)', marginBottom: 12 }}>Legal</div>
      <h1 style={{ fontSize: 'clamp(28px, 4vw, 48px)', marginBottom: 12, letterSpacing: '-0.02em' }}>{title}</h1>
      <p style={{ fontSize: 13, color: 'var(--ink-3)', fontFamily: 'var(--font-mono)', marginBottom: 48 }}>Last updated: June 2026</p>
      <div style={{ fontSize: 15, color: 'var(--ink-2)', lineHeight: 1.8 }}>
        {children}
      </div>
    </div>

    <footer style={{ borderTop: '1px solid var(--line)', padding: '24px', textAlign: 'center', fontSize: 12, color: 'var(--ink-4)', fontFamily: 'var(--font-mono)' }}>
      © CROPTILL LLC
    </footer>
  </div>
);

const Section = ({ title, children }) => (
  <div style={{ marginBottom: 40 }}>
    <h2 style={{ fontSize: 18, fontWeight: 600, color: 'var(--ink)', marginBottom: 12, marginTop: 0 }}>{title}</h2>
    {children}
  </div>
);

export const Privacy = () => (
  <LegalPage title="Privacy Policy">
    <Section title="1. Overview">
      <p>CROPTILL LLC ("we," "us," or "our") operates the website <strong>ventyvip.com</strong> (the "Site"). This Privacy Policy explains what personal information we collect, how we use and share it, and your rights regarding that information. By using the Site, you consent to the practices described in this policy.</p>
    </Section>

    <Section title="2. Information We Collect">
      <p><strong>Information you provide directly:</strong></p>
      <ul style={{ paddingLeft: 20, marginTop: 8, display: 'grid', gap: 6 }}>
        <li><strong>Email address</strong> — when you subscribe to our newsletter or submit an organizer inquiry</li>
        <li><strong>Name and contact details</strong> — when you submit an organizer contact form, consultation request, or photography booking</li>
        <li><strong>Event URLs</strong> — when you paste an event URL to generate a guide. These URLs are sent to our AI processing service (Google Gemini) and are not stored on our servers</li>
      </ul>

      <p style={{ marginTop: 16 }}><strong>Information collected automatically:</strong></p>
      <ul style={{ paddingLeft: 20, marginTop: 8, display: 'grid', gap: 6 }}>
        <li><strong>Device and browser information</strong> — browser type, operating system, device type, screen resolution, and language preferences</li>
        <li><strong>Usage data</strong> — pages visited, time spent on pages, click patterns, referring URL, and navigation paths</li>
        <li><strong>IP address and approximate location</strong> — used for analytics, fraud prevention, and ad personalization</li>
        <li><strong>Cookies and similar technologies</strong> — see our <Link to="/cookies" style={{ color: 'var(--primary)' }}>Cookie Policy</Link> for details</li>
      </ul>

      <p style={{ marginTop: 16 }}><strong>Information collected by third-party advertising partners:</strong></p>
      <ul style={{ paddingLeft: 20, marginTop: 8, display: 'grid', gap: 6 }}>
        <li><strong>Google AdSense / Google Ads</strong> — may collect device identifiers, browsing behavior, and ad interaction data to serve personalized ads and measure ad performance</li>
        <li><strong>Meta (Facebook/Instagram) Pixel</strong> — may collect browsing activity, page views, and conversion events to deliver targeted ads on Meta platforms and measure campaign effectiveness</li>
      </ul>
    </Section>

    <Section title="3. Legal Basis for Processing (GDPR)">
      <p>If you are located in the European Economic Area (EEA), United Kingdom, or Switzerland, we process your personal data under the following legal bases:</p>
      <ul style={{ paddingLeft: 20, marginTop: 8, display: 'grid', gap: 6 }}>
        <li><strong>Consent</strong> — for newsletter subscriptions, non-essential cookies, and personalized advertising</li>
        <li><strong>Legitimate interest</strong> — for website analytics, fraud prevention, and improving our services</li>
        <li><strong>Contractual necessity</strong> — for processing organizer inquiries and service requests</li>
        <li><strong>Legal obligation</strong> — for compliance with applicable laws and regulations</li>
      </ul>
    </Section>

    <Section title="4. How We Use Your Information">
      <p>We use the information we collect to:</p>
      <ul style={{ paddingLeft: 20, marginTop: 8, display: 'grid', gap: 6 }}>
        <li>Generate AI-powered event guides from URLs you submit</li>
        <li>Send our newsletter to subscribers who opt in</li>
        <li>Respond to organizer inquiries and service requests</li>
        <li>Display advertisements through Google AdSense and other ad networks</li>
        <li>Measure and improve advertising effectiveness through Meta Pixel and Google Ads conversion tracking</li>
        <li>Analyze site usage to improve features and performance</li>
        <li>Prevent fraud, abuse, and unauthorized access</li>
        <li>Comply with legal obligations</li>
      </ul>
    </Section>

    <Section title="5. How We Share Your Information">
      <p>We do <strong>not</strong> sell your personal information. We may share information with the following categories of third parties:</p>
      <ul style={{ paddingLeft: 20, marginTop: 8, display: 'grid', gap: 6 }}>
        <li><strong>Google</strong> (AdSense, Google Ads, Analytics) — for advertising, ad measurement, and site analytics. Google may use cookies and device identifiers to personalize ads. See <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--primary)' }}>Google's Privacy Policy</a></li>
        <li><strong>Meta Platforms</strong> (Facebook/Instagram Pixel) — for ad targeting, measurement, and conversion tracking on Meta platforms. See <a href="https://www.facebook.com/privacy/policy/" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--primary)' }}>Meta's Privacy Policy</a></li>
        <li><strong>Google Gemini API</strong> — event URLs you submit are processed by Google's AI services to generate guides. See <a href="https://ai.google.dev/terms" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--primary)' }}>Google AI Terms</a></li>
        <li><strong>Vercel</strong> — our hosting provider, which processes server logs. See <a href="https://vercel.com/legal/privacy-policy" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--primary)' }}>Vercel's Privacy Policy</a></li>
        <li><strong>Resend</strong> — our email delivery provider for transactional emails. See <a href="https://resend.com/legal/privacy-policy" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--primary)' }}>Resend's Privacy Policy</a></li>
      </ul>
      <p style={{ marginTop: 12 }}>We may also disclose information if required by law, regulation, legal process, or governmental request.</p>
    </Section>

    <Section title="6. Advertising & Personalized Ads">
      <p>VentyVIP displays advertisements served by <strong>Google AdSense</strong> and other advertising networks. These partners may use cookies, web beacons, and similar technologies to:</p>
      <ul style={{ paddingLeft: 20, marginTop: 8, display: 'grid', gap: 6 }}>
        <li>Serve ads based on your prior visits to this site or other websites</li>
        <li>Measure the effectiveness of advertising campaigns</li>
        <li>Build interest-based advertising profiles</li>
      </ul>
      <p style={{ marginTop: 12 }}>You can opt out of personalized advertising by visiting:</p>
      <ul style={{ paddingLeft: 20, marginTop: 8, display: 'grid', gap: 6 }}>
        <li><a href="https://adssettings.google.com" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--primary)' }}>Google Ads Settings</a></li>
        <li><a href="https://www.facebook.com/adpreferences" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--primary)' }}>Meta Ad Preferences</a></li>
        <li><a href="https://optout.aboutads.info" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--primary)' }}>DAA Opt-Out</a> (Digital Advertising Alliance)</li>
        <li><a href="https://www.youronlinechoices.eu" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--primary)' }}>Your Online Choices</a> (EU visitors)</li>
        <li><a href="https://optout.networkadvertising.org" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--primary)' }}>NAI Opt-Out</a> (Network Advertising Initiative)</li>
      </ul>
    </Section>

    <Section title="7. Cookies">
      <p>We use cookies and similar tracking technologies for essential site functionality, analytics, and advertising. For a detailed breakdown of the cookies we use, how to manage them, and your choices, please see our <Link to="/cookies" style={{ color: 'var(--primary)' }}>Cookie Policy</Link>.</p>
    </Section>

    <Section title="8. Data Retention">
      <ul style={{ paddingLeft: 20, marginTop: 8, display: 'grid', gap: 6 }}>
        <li><strong>Newsletter emails</strong> — retained until you unsubscribe</li>
        <li><strong>Organizer inquiries</strong> — retained for up to 24 months</li>
        <li><strong>Server logs</strong> — retained for up to 30 days</li>
        <li><strong>Advertising data</strong> — retention is governed by the respective advertising partner's policies (Google, Meta)</li>
      </ul>
    </Section>

    <Section title="9. International Data Transfers">
      <p>Your information may be transferred to and processed in countries other than your own, including the United States. These countries may have data protection laws different from your jurisdiction. We rely on standard contractual clauses and other legally approved transfer mechanisms to protect your data when it is transferred internationally.</p>
    </Section>

    <Section title="10. Your Privacy Rights">
      <p>Depending on your location, you may have the following rights:</p>

      <p style={{ marginTop: 16, fontWeight: 600, color: 'var(--ink)' }}>All Users:</p>
      <ul style={{ paddingLeft: 20, marginTop: 8, display: 'grid', gap: 6 }}>
        <li>Access, correct, or delete your personal data</li>
        <li>Unsubscribe from our newsletter via the link in any email</li>
        <li>Opt out of personalized advertising (see Section 6)</li>
        <li>Manage cookie preferences via your browser settings</li>
      </ul>

      <p style={{ marginTop: 16, fontWeight: 600, color: 'var(--ink)' }}>GDPR (EEA/UK/Switzerland):</p>
      <ul style={{ paddingLeft: 20, marginTop: 8, display: 'grid', gap: 6 }}>
        <li>Right to data portability</li>
        <li>Right to restrict processing</li>
        <li>Right to object to processing</li>
        <li>Right to withdraw consent at any time</li>
        <li>Right to lodge a complaint with a supervisory authority</li>
      </ul>

      <p style={{ marginTop: 16, fontWeight: 600, color: 'var(--ink)' }}>CCPA/CPRA (California Residents):</p>
      <ul style={{ paddingLeft: 20, marginTop: 8, display: 'grid', gap: 6 }}>
        <li>Right to know what personal information is collected and how it is used</li>
        <li>Right to delete personal information</li>
        <li>Right to opt out of the "sale" or "sharing" of personal information</li>
        <li>Right to non-discrimination for exercising your rights</li>
      </ul>
      <p style={{ marginTop: 8 }}>We do not sell personal information as defined by the CCPA. To exercise any of these rights, contact us using the information below.</p>

      <p style={{ marginTop: 16, fontWeight: 600, color: 'var(--ink)' }}>Other U.S. State Privacy Laws (Virginia, Colorado, Connecticut, etc.):</p>
      <p style={{ marginTop: 4 }}>Residents of states with comprehensive privacy laws may have similar rights to access, delete, correct, and opt out of targeted advertising. Contact us to exercise these rights.</p>
    </Section>

    <Section title="11. Children's Privacy">
      <p>VentyVIP is not directed to children under 13 (or under 16 in the EEA). We do not knowingly collect personal information from children. If you believe a child has provided us with personal information, please contact us and we will promptly delete it.</p>
    </Section>

    <Section title="12. Do Not Track">
      <p>Some browsers offer a "Do Not Track" (DNT) setting. There is currently no industry standard for responding to DNT signals. Our third-party advertising partners (Google, Meta) may or may not respond to DNT signals according to their own policies.</p>
    </Section>

    <Section title="13. Changes to This Policy">
      <p>We may update this Privacy Policy from time to time. When we make material changes, we will update the "Last updated" date at the top of this page. Your continued use of the Site after changes constitutes acceptance of the updated policy.</p>
    </Section>

    <Section title="14. Contact Us">
      <p>For privacy-related questions, data requests, or to exercise your rights, please contact us:</p>
      <p style={{ marginTop: 8 }}><strong>Email:</strong> <a href="mailto:info@VentyVIP.com" style={{ color: 'var(--primary)' }}>info@VentyVIP.com</a></p>
      <p style={{ marginTop: 4 }}><strong>Website:</strong> <a href="https://ventyvip.com" style={{ color: 'var(--primary)' }}>ventyvip.com</a></p>
    </Section>

    <Section title="15. Company Information">
      <p>VentyVIP is owned and operated by CROPTILL LLC.</p>
      <p style={{ marginTop: 8 }}>© 2026 CROPTILL LLC. All rights reserved.</p>
    </Section>
  </LegalPage>
);
